import {
  ensureFirestoreAuthReady,
  isFirestorePermissionDeniedError,
} from "./firestoreAuthReady";

/**
 * Share links load another teacher's public content while the viewer is
 * signed out, or signed in as someone else. Skip the owner-uid match there.
 * Teacher Present still requires a matching Auth user before subscribing.
 */
export function shouldRequireMatchingAuthUid({ isShareSession } = {}) {
  return isShareSession !== true;
}

/**
 * Starts a Firestore subscription only after Auth has restored its user and
 * retries one permission-denied handoff with a freshly minted ID token.
 *
 * Pass `requireMatchingUid: false` for public share reads so anonymous (and
 * non-owner) viewers can still subscribe after Auth has settled.
 */
export function subscribeWithFirestoreAuthRetry({
  auth,
  uid,
  subscribe,
  onAuthMismatch,
  onError,
  onRetry,
  requireMatchingUid = true,
}) {
  let isCancelled = false;
  let unsubscribeSnapshot = () => {};
  let subscriptionGeneration = 0;
  let didRetryAuthHandoff = false;

  const stopCurrentSubscription = () => {
    subscriptionGeneration += 1;
    unsubscribeSnapshot();
    unsubscribeSnapshot = () => {};
  };

  const startSubscription = async ({ forceAuthRefresh = false } = {}) => {
    try {
      const authUser = await ensureFirestoreAuthReady(auth, {
        forceRefresh: forceAuthRefresh,
      });
      if (isCancelled) return;

      if (requireMatchingUid && (!authUser || authUser.uid !== uid)) {
        onAuthMismatch?.(authUser || null);
        return;
      }

      const generation = subscriptionGeneration + 1;
      subscriptionGeneration = generation;
      unsubscribeSnapshot = subscribe((error) => {
        if (isCancelled || generation !== subscriptionGeneration) return;

        if (!didRetryAuthHandoff && isFirestorePermissionDeniedError(error)) {
          didRetryAuthHandoff = true;
          stopCurrentSubscription();
          onRetry?.();
          void startSubscription({ forceAuthRefresh: true });
          return;
        }

        stopCurrentSubscription();
        onError?.(error);
      });
    } catch (error) {
      if (!isCancelled) {
        onError?.(error);
      }
    }
  };

  void startSubscription();

  return () => {
    isCancelled = true;
    stopCurrentSubscription();
  };
}
